/**
 * Anonymous device identity, held entirely on this device.
 *
 * A keypair is generated in the browser with WebCrypto. The private key is
 * created NON-EXTRACTABLE, which means the browser will sign with it but will
 * never hand the bytes to JavaScript — not to our code, not to anything that
 * gets injected into our page. It is stored in IndexedDB as an opaque CryptoKey.
 *
 * The consequence is worth stating plainly: we cannot impersonate a user, we
 * cannot export their key, and we cannot revoke on their behalf. That is the
 * point. The ability to withdraw is the thing people are trusting us with, so
 * the withdrawal has to be signed by something only they hold.
 */

import { newNonce, toBase64Url } from '@omnialera/ori-protocol';

const DB_NAME = 'omnialera-ori';
const DB_VERSION = 1;
const STORE = 'identity';
const KEY_ID = 'device-key-v1';

const ALGORITHM = { name: 'ECDSA', namedCurve: 'P-256' };
const SIGN_PARAMS = { name: 'ECDSA', hash: 'SHA-256' };

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function sha256Base64Url(bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return toBase64Url(new Uint8Array(digest));
}

function toStandardBase64(bytes) {
  let binary = '';
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
  return btoa(binary);
}

let cached = null;

/**
 * Load the device identity, creating one on first run.
 *
 * Throws rather than silently degrading if WebCrypto or IndexedDB is missing:
 * an app that cannot sign cannot honour a revocation, and pretending otherwise
 * would be the worst possible failure mode.
 */
export async function loadIdentity() {
  if (cached) return cached;

  if (!globalThis.crypto?.subtle) {
    throw new Error('This browser does not provide WebCrypto over a secure context (https or localhost).');
  }
  if (!globalThis.indexedDB) {
    throw new Error('This browser does not provide IndexedDB, so a device key cannot be stored.');
  }

  const db = await openDb();
  let record = await idbGet(db, KEY_ID);

  if (!record) {
    const pair = await crypto.subtle.generateKey(ALGORITHM, /* extractable */ false, ['sign', 'verify']);
    const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
    record = {
      privateKey: pair.privateKey, // non-extractable CryptoKey; structured-cloned into IndexedDB
      publicKey: pair.publicKey,
      spki,
      createdAt: new Date().toISOString(),
    };
    await idbPut(db, KEY_ID, record);
  }

  const spkiBytes = new Uint8Array(record.spki);
  cached = {
    deviceId: await sha256Base64Url(spkiBytes),
    publicKey: toStandardBase64(spkiBytes),
    createdAt: record.createdAt,
    /** Sign a canonical string; returns base64url of the raw r‖s signature. */
    async signBytes(message) {
      const sig = await crypto.subtle.sign(SIGN_PARAMS, record.privateKey, message);
      return toBase64Url(new Uint8Array(sig));
    },
  };
  db.close();
  return cached;
}

/**
 * Destroy the device key.
 *
 * After this the device can never sign again as that identity — including to
 * revoke consent — so the caller must revoke everything first. `clearAll` in
 * record.js does exactly that, in the right order.
 */
export async function destroyIdentity() {
  cached = null;
  const db = await openDb();
  await idbDelete(db, KEY_ID);
  db.close();
}

export function freshNonce() {
  return newNonce(globalThis.crypto);
}
